const normalizeText = value => {
  return typeof value === 'string' ? value.trim() : '';
};

const normalizeCacheKey = value => {
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

export const normalizeBrandLogoUrl = value => {
  const sanitized = normalizeText(value);
  if (!sanitized) return '';

  if (
    sanitized.startsWith('data:image/') ||
    sanitized.startsWith('/') ||
    sanitized.startsWith('//')
  ) {
    return sanitized;
  }

  try {
    const parsedUrl = new URL(sanitized);
    if (['http:', 'https:'].includes(parsedUrl.protocol)) {
      return parsedUrl.toString();
    }
  } catch {
    return '';
  }

  return '';
};

const appendCacheBuster = (url, cacheKey) => {
  const normalizedUrl = normalizeBrandLogoUrl(url);
  const normalizedKey = normalizeCacheKey(cacheKey);

  if (
    !normalizedUrl ||
    !normalizedKey ||
    normalizedUrl.startsWith('data:image/')
  ) {
    return normalizedUrl;
  }

  const hashIndex = normalizedUrl.indexOf('#');
  const baseUrl =
    hashIndex === -1 ? normalizedUrl : normalizedUrl.slice(0, hashIndex);
  const hash = hashIndex === -1 ? '' : normalizedUrl.slice(hashIndex);
  const separator = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${separator}v=${encodeURIComponent(normalizedKey)}${hash}`;
};

const resolveLogoCacheKey = (account, globalConfig = {}) => {
  return normalizeCacheKey(
    account?.logo_cache_key ||
      account?.updated_at ||
      globalConfig.accountLogoCacheBuster ||
      globalConfig.ACCOUNT_LOGO_CACHE_BUSTER ||
      globalConfig.gitSha ||
      globalConfig.GIT_SHA ||
      globalConfig.appVersion ||
      globalConfig.APP_VERSION
  );
};

export const getAccountBrandName = account => {
  return normalizeText(account?.name);
};

export const getAccountBrandLogo = (
  account,
  globalConfig = {},
  { allowGlobalAccountFallback = true } = {}
) => {
  const scopedAccountLogo = normalizeBrandLogoUrl(
    account?.logo || account?.custom_attributes?.logo
  );
  if (scopedAccountLogo) {
    return scopedAccountLogo;
  }

  if (allowGlobalAccountFallback) {
    const currentAccountLogo = normalizeBrandLogoUrl(
      globalConfig.accountLogo || globalConfig.ACCOUNT_LOGO
    );
    if (currentAccountLogo) {
      return currentAccountLogo;
    }
  }

  return normalizeBrandLogoUrl(
    globalConfig.logoThumbnail || globalConfig.LOGO_THUMBNAIL
  );
};

export const resolveAccountBranding = (account, globalConfig = {}) => {
  return {
    title:
      getAccountBrandName(account) ||
      normalizeText(globalConfig.accountName || globalConfig.ACCOUNT_NAME) ||
      normalizeText(
        globalConfig.installationName || globalConfig.INSTALLATION_NAME
      ),
    logoUrl: getAccountBrandLogo(account, globalConfig),
    logoCacheKey: resolveLogoCacheKey(account, globalConfig),
  };
};

const faviconLinks = () =>
  Array.from(document.querySelectorAll('link[data-managed-favicon="true"]'));

export const syncAccountBrandingInDom = ({ title, logoUrl, logoCacheKey }) => {
  if (typeof document === 'undefined') return;

  const nextTitle = normalizeText(title);
  if (nextTitle) {
    document.title = nextTitle;
  }

  const nextLogoUrl = normalizeBrandLogoUrl(logoUrl);
  if (!nextLogoUrl) return;

  const cacheBustedLogoUrl = appendCacheBuster(nextLogoUrl, logoCacheKey);
  if (!cacheBustedLogoUrl) return;

  faviconLinks().forEach(link => {
    link.dataset.baseHref = cacheBustedLogoUrl;
    link.href = cacheBustedLogoUrl;
  });

  // Also update or create the shortcut icon that browsers use for the tab.
  // Some browsers ignore <link rel="icon"> and only look at <link rel="shortcut icon">
  // or cache /favicon.ico; this ensures the tab favicon reflects the brand logo.
  let shortcutLink = document.querySelector(
    'link[rel="shortcut icon"][data-brand-favicon="true"]'
  );
  if (!shortcutLink) {
    shortcutLink = document.createElement('link');
    shortcutLink.rel = 'shortcut icon';
    shortcutLink.dataset.brandFavicon = 'true';
    document.head.appendChild(shortcutLink);
  }
  shortcutLink.href = cacheBustedLogoUrl;
};
